import React, { useEffect, useRef } from "react";
import { extractYouTubeId } from "../lib/video";

// MP4 background that reliably reloads when the source changes.
// (Browsers don't reload a <video> when a <source> child's src changes — must call load().)
function Mp4Background({ source, poster }) {
  const ref = useRef(null);
  useEffect(() => {
    const v = ref.current;
    if (v) {
      v.load();
      const p = v.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    }
  }, [source]);
  return (
    <div className="absolute inset-0 overflow-hidden">
      <video
        ref={ref}
        key={source}
        className="absolute inset-0 w-full h-full object-cover"
        autoPlay
        muted
        loop
        playsInline
        poster={poster}
        src={source}
      />
    </div>
  );
}

// Full-cover background. Supports YouTube (id or url), direct MP4 url, and static image.
export default function VideoBackground({ type = "youtube", source, poster }) {
  if (type === "image") {
    return (
      <div className="absolute inset-0">
        <img src={source || poster} alt="" className="w-full h-full object-cover" />
      </div>
    );
  }

  if (type === "mp4" && source) {
    return <Mp4Background source={source} poster={poster} />;
  }

  const id = extractYouTubeId(source || "");
  if (!id) {
    return poster ? (
      <div className="absolute inset-0">
        <img src={poster} alt="" className="w-full h-full object-cover" />
      </div>
    ) : null;
  }

  const src =
    `https://www.youtube-nocookie.com/embed/${id}` +
    `?autoplay=1&mute=1&controls=0&loop=1&playlist=${id}` +
    `&playsinline=1&modestbranding=1&rel=0&showinfo=0&disablekb=1&iv_load_policy=3`;

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {poster && <img src={poster} alt="" className="absolute inset-0 w-full h-full object-cover" />}
      <iframe
        title="background-video"
        src={src}
        allow="autoplay; encrypted-media; picture-in-picture"
        frameBorder="0"
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[177.78vh] min-w-full h-[56.25vw] min-h-full"
      />
    </div>
  );
}
